import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { InfiniteQueryObserverResult, UseMutateFunction } from 'react-query';
import { VoicemailTypes } from '@frontend/api-voicemails';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import { VoicemailInfiniteQueryData, VoicemailTableDataRow } from '../components/all-calls/types';
import { VoicemailDetailsAccordionType } from '../components/voicemail-details/types';
import { getTranscriptionUniqueKey } from '../components/voicemail-details/utils';

interface VoicemailDetailsStore {
  // Core context data
  voicemailData?: VoicemailTableDataRow;
  rows: VoicemailTableDataRow[];
  pageSize: number;
  instanceId: string;
  currentRowId: string;
  sectionFocus?: VoicemailDetailsAccordionType;
  voicemailsQueryKey: string[];
  userHasAccess: boolean;
  prioritizeQueryCache?: boolean;
  isDetached: boolean;

  // Functions
  toggleRowSelected?: (id: string, set: boolean, index?: number) => void;
  updateReadStatus?: UseMutateFunction<any, unknown, VoicemailTypes.StatusPayload, unknown>;
  deleteVoicemail?: UseMutateFunction<any, unknown, VoicemailTypes.DeletePayload, void>;
  optimisticApplyTag?: (args: { oldData: any | undefined; tag: Tag; voicemailId: string }) => any;
  optimisticRemoveTag?: (args: { oldData: any | undefined; tag: Tag; voicemailId: string }) => any;
  getNextPage?: () => Promise<InfiniteQueryObserverResult<VoicemailInfiniteQueryData, unknown>>;

  // Store actions
  setVoicemailData: (voicemailData?: VoicemailTableDataRow) => void;
  setRows: (rows: VoicemailTableDataRow[]) => void;
  setPageSize: (pageSize: number) => void;
  setInstanceId: (instanceId: string) => void;
  setCurrentRowId: (currentRowId: string) => void;
  setSectionFocus: (sectionFocus?: VoicemailDetailsAccordionType) => void;
  setVoicemailsQueryKey: (voicemailsQueryKey: string[]) => void;
  setUserHasAccess: (userHasAccess: boolean) => void;
  setPrioritizeQueryCache: (prioritizeQueryCache?: boolean) => void;
  setToggleRowSelected: (toggleRowSelected: (id: string, set: boolean, index?: number) => void) => void;
  setUpdateReadStatus: (
    updateReadStatus?: UseMutateFunction<any, unknown, VoicemailTypes.StatusPayload, unknown>
  ) => void;
  setDeleteVoicemail: (deleteVoicemail?: UseMutateFunction<any, unknown, VoicemailTypes.DeletePayload, void>) => void;
  setOptimisticApplyTag: (
    optimisticApplyTag: (args: { oldData: any | undefined; tag: Tag; voicemailId: string }) => any
  ) => void;
  setOptimisticRemoveTag: (
    optimisticRemoveTag: (args: { oldData: any | undefined; tag: Tag; voicemailId: string }) => any
  ) => void;
  setGetNextPage: (
    getNextPage?: () => Promise<InfiniteQueryObserverResult<VoicemailInfiniteQueryData, unknown>>
  ) => void;

  // Helper methods
  getCurrentRow: () => VoicemailTableDataRow | undefined;
  getNextRow: () => { row: VoicemailTableDataRow; index: number } | undefined;
  getPreviousRow: () => { row: VoicemailTableDataRow; index: number } | undefined;
  navigateToNext: () => void;
  navigateToPrevious: () => void;
  setIsDetached: (isDetached: boolean) => void;

  // Reset function
  reset: () => void;
}

const initialState = {
  rows: [],
  pageSize: 25,
  instanceId: '',
  currentRowId: '',
  voicemailData: undefined,
  sectionFocus: undefined,
  voicemailsQueryKey: [],
  userHasAccess: true,
  isDetached: true,
  prioritizeQueryCache: false,
  toggleRowSelected: undefined,
  updateReadStatus: undefined,
  deleteVoicemail: undefined,
  optimisticApplyTag: undefined,
  optimisticRemoveTag: undefined,
  getNextPage: undefined,
};

export const useVoicemailDetailsStore = createStoreWithSubscribe<VoicemailDetailsStore>(
  (set, get) => ({
    ...initialState,

    // Store actions
    setVoicemailData: (voicemailData) => set({ voicemailData }),
    setRows: (rows) => set({ rows }),
    setPageSize: (pageSize) => set({ pageSize }),
    setInstanceId: (instanceId) => set({ instanceId }),
    setCurrentRowId: (currentRowId) => set({ currentRowId }),
    setSectionFocus: (sectionFocus) => set({ sectionFocus }),
    setVoicemailsQueryKey: (voicemailsQueryKey) => set({ voicemailsQueryKey }),
    setUserHasAccess: (userHasAccess) => set({ userHasAccess }),
    setPrioritizeQueryCache: (prioritizeQueryCache) => set({ prioritizeQueryCache }),
    setToggleRowSelected: (toggleRowSelected) => set({ toggleRowSelected }),
    setUpdateReadStatus: (updateReadStatus) => set({ updateReadStatus }),
    setDeleteVoicemail: (deleteVoicemail) => set({ deleteVoicemail }),
    setOptimisticApplyTag: (optimisticApplyTag) => set({ optimisticApplyTag }),
    setOptimisticRemoveTag: (optimisticRemoveTag) => set({ optimisticRemoveTag }),
    setGetNextPage: (getNextPage) => set({ getNextPage }),
    setIsDetached: (isDetached) => set({ isDetached }),

    // Helper methods
    getCurrentRow: () => {
      const { rows, currentRowId } = get();
      return rows.find((row) => getTranscriptionUniqueKey(row) === currentRowId);
    },

    getNextRow: () => {
      const { rows, currentRowId } = get();
      const currentIndex = rows.findIndex((row) => getTranscriptionUniqueKey(row) === currentRowId);
      if (currentIndex !== -1 && currentIndex < rows.length - 1) {
        return { row: rows[currentIndex + 1], index: currentIndex + 1 };
      }
      return undefined;
    },

    getPreviousRow: () => {
      const { rows, currentRowId } = get();
      const currentIndex = rows.findIndex((row) => getTranscriptionUniqueKey(row) === currentRowId);
      if (currentIndex > 0) {
        return { row: rows[currentIndex - 1], index: currentIndex - 1 };
      }
      return undefined;
    },

    navigateToNext: () => {
      const { getNextRow, toggleRowSelected } = get();
      const nextRow = getNextRow();
      if (nextRow && toggleRowSelected) {
        const nextRowId = getTranscriptionUniqueKey(nextRow.row);
        set({ currentRowId: nextRowId, voicemailData: nextRow.row });
        toggleRowSelected(nextRowId, true, nextRow.index);
      }
    },

    navigateToPrevious: () => {
      const { getPreviousRow, toggleRowSelected } = get();
      const prevRow = getPreviousRow();
      if (prevRow && toggleRowSelected) {
        const prevRowId = getTranscriptionUniqueKey(prevRow.row);
        set({ currentRowId: prevRowId, voicemailData: prevRow.row });
        toggleRowSelected(prevRowId, true, prevRow.index);
      }
    },

    // Reset function
    reset: () => set(initialState),
  }),
  {
    name: 'transcription-context-store',
  }
);

export const useVoicemailDetailsShallowStore = createShallowStore(useVoicemailDetailsStore);
